import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatNumber } from '../../lib/format.js';
import { Badge, CardHead, Empty, ErrorBanner, SubHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { callSnippet } from '../dialler/apiSnippet.js';

/**
 * Click-to-dial from a CRM (0174, 29 October 2026): the API clients with the scope calls:dial, and
 * the calls they asked for. A request rings the seat's phones first; when the person picks up, the
 * number is dialled as that seat, under its routes, dial policy, caller ID and credit.
 */

interface Client {
  id: string; name: string; secret_hint: string; enabled: boolean; uses: number;
  last_used_at: string | null; created_at: string;
}
interface LinkedBrowser {
  id: string; label: string; extension: string; display_name: string | null; created_at: string; last_used_at: string | null;
}
interface Call {
  id: string; extension: string | null; to: string; reference: string | null; status: string; cause: string | null;
  created_at: string; ringing_at: string | null; answered_at: string | null; ended_at: string | null; requested_by: string;
}

const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' | 'muted' }> = {
  queued: { label: 'Waiting', tone: 'info' },
  ringing: { label: 'Ringing the seat', tone: 'info' },
  answered: { label: 'Connected', tone: 'ok' },
  no_answer: { label: 'Seat did not answer', tone: 'warn' },
  busy: { label: 'Seat busy', tone: 'warn' },
  failed: { label: 'Failed', tone: 'danger' },
  expired: { label: 'Not placed', tone: 'danger' },
  cancelled: { label: 'Called off', tone: 'muted' },
};
const when = (s: string | null) => (s ? new Date(s).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export function ClickToDialTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const mayWrite = can('apikey:write');
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/click-to-dial`;
  const key = ['click-to-dial', tenantId];
  const data = useQuery({
    queryKey: key, queryFn: () => api.get<{ clients: Client[]; calls: Call[] }>(base), refetchInterval: 10_000,
  });
  const done = () => void qc.invalidateQueries({ queryKey: key });
  const create = useMutation({
    mutationFn: (name: string) => api.post<Client & { secret: string }>(`${base}/clients`, { name }), onSuccess: done,
  });
  const update = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) => api.patch(`${base}/clients/${v.id}`, { enabled: v.enabled }), onSuccess: done,
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.delete(`${base}/clients/${id}`), onSuccess: done });
  /** Browsers linked to a seat by ArcSIP's QR scan (0175): the ArcNet Chrome extension. */
  const linksBase = `/v1/tenants/${tenantId}/browser-links`;
  const links = useQuery({ queryKey: ['browser-links', tenantId], queryFn: () => api.get<{ data: LinkedBrowser[] }>(linksBase) });
  const unlink = useMutation({
    mutationFn: (id: string) => api.delete(`${linksBase}/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['browser-links', tenantId] }),
  });
  const step = useStepUp();
  const [name, setName] = useState('');
  const [made, setMade] = useState<{ id: string; name: string; secret: string } | null>(null);
  const clients = data.data?.clients;
  const calls = data.data?.calls;

  return (
    <>
      {made && <SecretShown made={made} onClose={() => setMade(null)} />}

      <div className="arc-card" style={{ marginBottom: 16 }}>
        <CardHead title="Click-to-dial clients"
          sub="A CRM or your own server asks for a call; the seat's phones ring, and when they pick up the number is dialled as that seat." />
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Client</th><th>Client id</th><th className="arc-td-right">Calls</th><th>Last used</th><th>On</th><th /></tr></thead>
            {clients === undefined ? <TableSkeleton query={data} cols={6} /> : (
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{c.name}</span>
                      <div className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>secret …{c.secret_hint}</div>
                    </td>
                    <td className="arc-mono" style={{ fontSize: 12 }}>{c.id}</td>
                    <td className="arc-td-right arc-num">{formatNumber(c.uses)}</td>
                    <td className="arc-num">{when(c.last_used_at)}</td>
                    <td>
                      <Switch id={`ctd-${c.id}`} checked={c.enabled} disabled={!mayWrite || update.isPending}
                        onChange={(v) => update.mutate({ id: c.id, enabled: v })} />
                    </td>
                    <td className="arc-td-right">
                      {mayWrite && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { void confirmAction({ title: `Revoke ${c.name}?`, body: 'Its tokens stop working at once.', action: 'Revoke', danger: true }).then((ok) => { if (ok) void step.run(() => revoke.mutateAsync(c.id)); }); }}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {clients && clients.length === 0 && (
          <Empty icon="external" title="Nothing connected" hint="Make a client for each CRM or server that should be able to start calls." />
        )}
        {mayWrite && (
          <form className="arc-card-body" style={{ borderTop: '1px solid var(--arc-border)' }} onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void step.run(() => create.mutateAsync(name.trim()))
              .then((r) => { if (r) { setMade({ id: r.id, name: r.name, secret: r.secret }); setName(''); } });
          }}>
            <SubHead>New client</SubHead>
            <div className="arc-row" style={{ gap: 8, alignItems: 'end' }}>
              <div className="arc-field" style={{ margin: 0, flex: '0 1 420px' }}>
                <label className="arc-label" htmlFor="ctd-name">Name</label>
                <input id="ctd-name" className="arc-input" value={name} maxLength={120} required
                  onChange={(e) => setName(e.target.value)} placeholder="HubSpot" />
              </div>
              <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending || step.busy || !name.trim()}>
                Make the client
              </button>
            </div>
          </form>
        )}
        {(create.error || update.error || revoke.error) && (
          <div className="arc-card-body"><ErrorBanner error={create.error ?? update.error ?? revoke.error} /></div>
        )}
      </div>

      <div className="arc-card" style={{ marginBottom: 16 }}>
        <CardHead title="Linked browsers"
          sub="The ArcNet Chrome extension, linked to one seat by scanning its code in ArcSIP. It can only ask for calls from that seat." />
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Seat</th><th>Browser</th><th>Linked</th><th>Last used</th><th /></tr></thead>
            {links.data === undefined ? <TableSkeleton query={links} cols={5} /> : (
              <tbody>
                {links.data.data.map((l) => (
                  <tr key={l.id}>
                    <td><span className="arc-mono">{l.extension}</span> {l.display_name ?? ''}</td>
                    <td>{l.label}</td>
                    <td className="arc-num">{when(l.created_at)}</td>
                    <td className="arc-num">{when(l.last_used_at)}</td>
                    <td className="arc-td-right">
                      {mayWrite && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { void confirmAction({ title: `Unlink ${l.label} from ${l.extension}?`, body: 'It stops working at once.', action: 'Unlink', danger: true }).then((ok) => { if (ok) void step.run(() => unlink.mutateAsync(l.id)); }); }}>
                          Unlink
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {links.data && links.data.data.length === 0 && (
          <Empty icon="phone" title="No browsers linked" hint="In the extension, choose Link with ArcSIP, then scan the code with the app." />
        )}
        {unlink.error && <div className="arc-card-body"><ErrorBanner error={unlink.error} /></div>}
      </div>

      <div className="arc-card">
        <CardHead title="Recent requests" sub="The last 50. The call after the seat picks up is in call history." />
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Asked</th><th>Seat</th><th>Number</th><th>From</th><th>Reference</th><th>What happened</th></tr></thead>
            {calls === undefined ? <TableSkeleton query={data} cols={6} /> : (
              <tbody>
                {calls.map((c) => {
                  const s = STATUS[c.status] ?? { label: c.status, tone: 'muted' as const };
                  return (
                    <tr key={c.id}>
                      <td className="arc-num">{when(c.created_at)}</td>
                      <td className="arc-mono">{c.extension ?? '—'}</td>
                      <td className="arc-mono">{c.to}</td>
                      <td>{c.requested_by}</td>
                      <td className="arc-mono" style={{ fontSize: 12 }}>{c.reference ?? '—'}</td>
                      <td>
                        <Badge tone={s.tone}>{s.label}</Badge>
                        {c.cause && c.status !== 'answered' && <span className="arc-subtle arc-mono" style={{ fontSize: 11.5, marginLeft: 6 }}>{c.cause}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {calls && calls.length === 0 && <Empty icon="phone" title="No requests yet" hint="Requests appear here as a client makes them." />}
      </div>

      <StepUpPrompt step={step} what="A click-to-dial client">
        Whoever holds its secret can ring any seat on this account and have it dial out, charged to the account.
      </StepUpPrompt>
    </>
  );
}

/** The id and secret, the secret this once, and the code that uses them. */
function SecretShown({ made, onClose }: { made: { id: string; name: string; secret: string }; onClose: () => void }) {
  const code = callSnippet({ base: `${window.location.origin}/api/v1`, clientId: made.id });
  const copy = (s: string) => void navigator.clipboard.writeText(s);
  return (
    <div className="arc-card arc-card-body" style={{ marginBottom: 16, borderLeft: '3px solid var(--arc-ok)' }}>
      <div className="arc-row" style={{ gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>{made.name}</strong>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onClose}>Done</button>
      </div>
      <p className="arc-muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        The secret is shown this once and is never sent over the wire: it signs in with SCRAM-SHA-256.
        A lost secret is replaced, not recovered.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '6px 10px', alignItems: 'center' }}>
        <span className="arc-label" style={{ margin: 0 }}>Client id</span>
        <code className="arc-mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>{made.id}</code>
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => copy(made.id)}>Copy</button>
        <span className="arc-label" style={{ margin: 0 }}>Secret</span>
        <code className="arc-mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>{made.secret}</code>
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => copy(made.secret)}>Copy</button>
      </div>
      <div className="arc-row" style={{ justifyContent: 'flex-end', margin: '12px 0 4px' }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => copy(code)}>Copy code</button>
      </div>
      <pre className="arc-mono" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', margin: 0, maxHeight: 360, overflow: 'auto' }}>{code}</pre>
    </div>
  );
}
